import React, { VFC } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from '../../contexts/translationContext';
import { theme } from '../../utils/theme';
import { IconVariants } from '../atoms/icons/types';

import SvgIcon from '../atoms/SvgIcon';
import Typography, { ITranslation } from '../atoms/Typography';
import { useSettings } from '../../contexts/settingsContext';

type FieldVariant = 'text' | 'email';

interface Props extends React.HTMLProps<HTMLInputElement>, ITranslation {
  title?: string;
  active?: boolean;
  borderColor?: string;
  mandatory?: boolean;
  type?: string;
  variant?: FieldVariant;
  error?: string;
  placeholder?: string;
  placeholderParams?: string;
  icon?: IconVariants;
  noTranslation?: boolean;
}

interface IStyledTextInput
  extends React.HTMLProps<HTMLInputElement | HTMLDivElement>,
    ITranslation {
  borderColor?: string;
  borderRadius?: string;
  disabled?: boolean;
  error?: string;
}

const InputWrapper = styled.div`
  width: 100%;
`;

const StyledTextInputContainer = styled.div<IStyledTextInput>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.grey[0]};
  border-radius: ${(props) => props.borderRadius};
  ${({ error }) =>
    error &&
    css`
      border: 1px solid ${theme.colors.red[100]};
    `}}
  :disabled {
    color: ${theme.colors.grey[20]};
    outline: none;
  }
`;

const StyledTextInput = styled.input<IStyledTextInput>`
  display: flex;
  width: 100%;
  background-color: transparent;
  border-radius: ${(props) => props.borderRadius};
  padding: 1rem 1.6rem;
  outline:none;
  ${({ error }) =>
    error &&
    css`
      border: none;
    `}}
    ${({ error }) =>
      !error &&
      css`
        :focus {
          border: 1px solid ${theme.colors.grey[100]};
        }
        border: 1px solid ${theme.colors.grey[40]}; ;
      `}}

  :placeholder {
    letter-spacing: -2%;
    font-family: ${theme.font};
  }
`;

const StyledErrorMsg = styled(Typography)`
  padding: 0 0 0 0.8rem;
  margin-top: 0.4rem;
  color: ${theme.colors.red[100]};
`;

const TextInput: VFC<Props> = ({
  value,
  title,
  placeholder,
  borderColor,
  disabled,
  type = 'text',
  mandatory = false,
  error,
  onChange,
  icon,
  noTranslation = false,
}) => {
  const { translate } = useTranslation();
  const { cornerRadius } = useSettings();

  const translatedPlaceholder = placeholder && translate(placeholder, null);
  return (
    <InputWrapper>
      <Typography translateKey={noTranslation ? undefined : title} variant="input-label">
        {mandatory ? `${title} *` : title}
      </Typography>
      <StyledTextInputContainer borderRadius={cornerRadius} error={error}>
        <StyledTextInput
          borderRadius={cornerRadius}
          value={value}
          type={type}
          disabled={disabled}
          borderColor={borderColor}
          placeholder={translatedPlaceholder}
          onChange={onChange}
          error={error}
        />
        {!!icon && !error && (
          <SvgIcon
            color={theme.colors.grey[100]}
            icon={icon}
            iconSize="small"
          />
        )}
        {error && (
          <SvgIcon
            color={theme.colors.red[100]}
            icon="cross"
            iconSize="small"
          />
        )}
      </StyledTextInputContainer>

      {!!error && (
        <StyledErrorMsg variant="input-label">{error}</StyledErrorMsg>
      )}
    </InputWrapper>
  );
};

export default TextInput;
